import React, { useContext } from "react";
import { EnumOptionsType, WidgetProps as RJSFWidgetProps } from "@rjsf/utils";
import { isNil } from "lodash";
import { Button } from "../Common/Button";
import { Tag } from "../Common/Tag";
import { InformationalTooltip } from "../Common/Tooltip";
import { Checkbox, Datepicker, Select, Text, Textarea } from "../Inputs";
import { LabelMeta, Legend } from "../Inputs/__styles__/Label";
import { Fieldset, Row } from "../Inputs/__styles__/Wrapper";
import { Input } from "./__styles__/Inputs";
import { RegenerateButtonWrapper, TagWrapper } from "./__styles__/Widget";
import { SubmissionFormContext } from "./Form";
import { castEmptyStringToNull, useDefaultedValue } from "./utils";
export type WidgetProps = RJSFWidgetProps & { isSubmissionBuilder?: boolean };

export const CustomSelect = (props: WidgetProps) => {
  const value = props.value ?? null;
  const tooltipText = props.uiSchema?.["ui:tooltipText"];

  return (
    <Input isSubmissionBuilder={props.isSubmissionBuilder}>
      <Select
        name={props.id}
        label={props.label}
        required={props.required && !props.disabled}
        value={value}
        onChange={value => props.onChange(isNil(value) ? undefined : value)}
        disabled={false}
        readonly={props.readonly || props.disabled}
        options={props.options.enumOptions!.map((option: EnumOptionsType) => {
          return { label: option.label, value: option.value };
        })}
        placeholder={props.placeholder}
        helperText={props.uiSchema?.["ui:helperText"]}
        error={props.rawErrors?.join(", ")}
        tooltip={tooltipText}
        css={!!tooltipText ? { width: "100%" } : {}}
        isClearable={true}
      />
    </Input>
  );
};

export const AIFieldLabelDecorator = ({
  onRegenerate,
  regenerateHidden,
  regenerateDisabled,
  regenerating,
}: {
  onRegenerate?: () => void;
  regenerateHidden: boolean;
  regenerateDisabled: boolean;
  regenerating: boolean;
}) => {
  return (
    <Row>
      <TagWrapper>
        <Tag leftIconName="sparkles" styleVariant="default">
          Generated with AI
        </Tag>
      </TagWrapper>
      {!regenerateHidden && (
        <RegenerateButtonWrapper>
          <Button
            onClick={onRegenerate}
            styleVariant={regenerateDisabled ? "ghostGrey" : "ghost"}
            disabled={regenerateDisabled}
            size="xSmall"
            type="button"
          >
            {regenerating ? "Regenerating..." : "Regenerate"}
          </Button>
        </RegenerateButtonWrapper>
      )}
    </Row>
  );
};

export const CustomTextarea = (props: WidgetProps) => {
  const value = props.value ?? null;
  const { loadingFormPaths } = useContext(SubmissionFormContext);

  const { editing, regenerateAIFields } = useContext(SubmissionFormContext);

  const isGenAIField = props.uiSchema?.["ui:useGenerativeAI"];
  const isFormulaField = !!props.schema.formula;
  const disabled = !editing && isGenAIField;
  const readonly = props.readonly || props.disabled || isFormulaField;
  const path = props.id.replace("root_", "").replace(/_/g, ".");

  const labelDecorator =
    isGenAIField ?
      <AIFieldLabelDecorator
        onRegenerate={() => {
          regenerateAIFields?.([path]);
        }}
        // we hide the regenerate button if the form is not equipped to generate AI responses
        // or the user cannot edit the field
        regenerateHidden={!regenerateAIFields || readonly}
        regenerateDisabled={Object.values(loadingFormPaths).some(
          value => value,
        )}
        regenerating={loadingFormPaths[path] ?? false}
      />
    : null;

  return (
    <Input isSubmissionBuilder={props.isSubmissionBuilder}>
      <Textarea
        name={props.id}
        label={props.label}
        required={props.required && !props.disabled}
        value={value}
        onChange={value => {
          props.onChange(
            castEmptyStringToNull({
              value,
              required: props.required ?? false,
            }),
          );
        }}
        disabled={disabled}
        readonly={readonly}
        placeholder={props.placeholder}
        helperText={props.uiSchema?.["ui:helperText"]}
        error={props.rawErrors?.join(", ")}
        labelSize={props.uiSchema?.["ui:labelSize"]}
        tooltip={props.uiSchema?.["ui:tooltipText"]}
        isGenAIField={isGenAIField}
        labelDecorator={labelDecorator}
      />
    </Input>
  );
};

export const CustomText = (props: WidgetProps) => {
  const value = props.value ?? null;
  const isFormulaField = !!props.schema.formula;

  return (
    <Input isSubmissionBuilder={props.isSubmissionBuilder}>
      <Text
        name={props.id}
        label={props.label}
        required={props.required && !props.disabled}
        value={value}
        onChange={value =>
          props.onChange(
            castEmptyStringToNull({
              value,
              required: props.required ?? false,
            }),
          )
        }
        disabled={false}
        readonly={props.readonly || props.disabled || isFormulaField}
        placeholder={props.placeholder}
        helperText={props.uiSchema?.["ui:helperText"]}
        error={props.rawErrors?.join(", ")}
        labelSize={props.uiSchema?.["ui:labelSize"]}
        tooltip={props.uiSchema?.["ui:tooltipText"]}
      />
    </Input>
  );
};

export const CustomDatePicker = (props: WidgetProps) => {
  const isFormulaField = !!props.schema.formula;
  const value = useDefaultedValue(props).value;

  return (
    <Input isSubmissionBuilder={props.isSubmissionBuilder}>
      <Datepicker
        name={props.id}
        label={props.label}
        required={props.required && !props.disabled}
        value={value}
        onChange={value =>
          value ? props.onChange(value) : props.onChange(null)
        }
        disabled={false}
        readonly={props.readonly || props.disabled || isFormulaField}
        helperText={props.uiSchema?.["ui:helperText"]}
        error={props.rawErrors?.join(", ")}
        placeholderText={props.placeholder}
        tooltip={props.uiSchema?.["ui:tooltipText"]}
      />
    </Input>
  );
};

export const DamageDegree = (props: WidgetProps) => {
  return <CustomSelect {...props} />;
};

export const CustomCheckbox = (props: WidgetProps) => {
  const value = props.value ?? null;
  const tooltipText = props.uiSchema?.["ui:tooltipText"];
  const isFormulaField = !!props.schema.formula;

  const tooltip =
    tooltipText ?
      <InformationalTooltip tooltipText={tooltipText} place="right" />
    : undefined;

  const hasTooltip = !!tooltip;

  return (
    <Input isSubmissionBuilder={props.isSubmissionBuilder}>
      <Fieldset>
        {/* 
          If you want to use functionality Wrapper provides, such as errors or tooltips,
          you will need to update the component to optionally use Legend instead of Label.
          Legend is semantically correct for checkboxes because it isn't associated with the input itself
        */}
        <Legend>
          {props.label.trim()}
          {hasTooltip && <LabelMeta>{tooltip}</LabelMeta>}
        </Legend>
      </Fieldset>
      <Checkbox
        name={props.id}
        value={value}
        onChange={props.onChange}
        disabled={false}
        readonly={props.readonly || props.disabled || isFormulaField}
        label={props.uiSchema?.["ui:checkboxText"]}
        required={props.required && !props.disabled}
      />
    </Input>
  );
};
